// src/pages/customer-panel/CustomerDashboard.js
// Customer Panel Dashboard — stat cards + charts from /dashboard-data

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    LinearProgress,
    Avatar,
    IconButton,
    Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AlarmIcon from '@mui/icons-material/Alarm';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import StarsIcon from '@mui/icons-material/Stars';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import axiosInstance from '../../utils/axiosInstance';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend,
    BarChart, Bar,
} from 'recharts';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4'];

const StatCard = ({ icon: Icon, title, value, color, subtitle, loading }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'grey.100',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
            },
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem',
                            textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1,
                        }}
                    >
                        {title}
                    </Typography>
                    {loading ? (
                        <CircularProgress size={24} sx={{ color }} />
                    ) : (
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                            {value ?? '--'}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', mt: 0.5, display: 'block' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const ChartCard = ({ title, icon, children, loading }) => (
    <Paper
        sx={{
            borderRadius: '16px', border: '1px solid', borderColor: 'grey.100',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
            display: 'flex', flexDirection: 'column',
        }}
    >
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'grey.100', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {icon}
            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ p: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 280 }}>
            {loading ? (
                <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
            ) : (
                children
            )}
        </Box>
    </Paper>
);

export default function CustomerDashboard() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const firstName = user?.profile?.fname || 'User';
    const lastName = user?.profile?.lname || '';
    const userRole = user?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'owner';
    const isStudent = userRole === 'student';

    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/dashboard-data');
            setDashboardData(response.data?.data || response.data);
        } catch (err) {
            console.error('Failed to fetch dashboard data:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Customer Dashboard | KlonIT';
        fetchDashboardData();
    }, [fetchDashboardData]);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    const widgets = dashboardData?.widgets || {};
    const charts = dashboardData?.charts || {};

    // --- Stat cards by role ---
    const adminStats = [
        { icon: PeopleIcon, title: 'Total Users', value: widgets.total_users ?? 0, color: '#3b82f6', subtitle: 'Active team members' },
        { icon: DescriptionIcon, title: 'Total Documents', value: widgets.total_documents ?? 0, color: '#10b981', subtitle: 'Uploaded files' },
        { icon: AlarmIcon, title: 'Total Reminders', value: widgets.total_reminders ?? 0, color: '#8b5cf6', subtitle: 'Scheduled reminders' },
        { icon: NotificationsActiveIcon, title: 'Notifications', value: widgets.total_notifications ?? 0, color: '#f59e0b', subtitle: 'Total notifications' },
    ];

    const regularStats = [
        { icon: DescriptionIcon, title: 'Total Documents', value: widgets.total_documents ?? 0, color: '#10b981', subtitle: 'Your uploaded files' },
        { icon: AlarmIcon, title: 'Total Reminders', value: widgets.total_reminders ?? 0, color: '#8b5cf6', subtitle: 'Your reminders' },
        { icon: NotificationsActiveIcon, title: 'Notifications', value: widgets.total_notifications ?? 0, color: '#f59e0b', subtitle: 'Total notifications' },
    ];

    const studentStats = [
        { icon: SchoolIcon, title: 'Active Courses', value: dashboardData?.active_courses ?? 0, color: '#3b82f6', subtitle: 'Currently enrolled' },
        { icon: CheckCircleIcon, title: 'Completed', value: dashboardData?.completed_courses ?? 0, color: '#10b981', subtitle: 'Finished courses' },
        { icon: CardMembershipIcon, title: 'Certificates', value: dashboardData?.certificates ?? 0, color: '#8b5cf6', subtitle: 'Earned to date' },
        { icon: StarsIcon, title: 'Total Points', value: dashboardData?.total_points ?? 0, color: '#f59e0b', subtitle: 'Accumulated XP' },
    ];

    const getStatCards = () => {
        if (isStudent) return studentStats;
        if (isAdmin) return adminStats;
        return regularStats;
    };

    const statCards = getStatCards();
    // For 3 cards use md={4} (3 per row), for 4 cards use md={3} (4 per row)
    const statMd = statCards.length === 3 ? 4 : 3;

    // --- Chart data ---
    const docsOverTime = charts.documents_over_time || null;
    const areaData = docsOverTime
        ? docsOverTime.categories.map((cat, i) => ({
            month: cat,
            documents: docsOverTime.series?.[0]?.data?.[i] ?? 0,
        }))
        : [];

    const docsPerUser = charts.documents_per_user || [];
    const remindersByStatus = (charts.reminders_by_status || []).map(item => ({
        ...item,
        name: (item.name || '').charAt(0) + (item.name || '').slice(1).toLowerCase(),
    }));

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Welcome Banner */}
            <Paper
                sx={{
                    p: { xs: 3, md: 4 }, mb: 3,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white', borderRadius: '20px',
                    position: 'relative', overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
                <Box sx={{ position: 'absolute', bottom: -60, right: 80, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)',
                                    fontSize: '1.4rem', fontFamily, fontWeight: 700,
                                }}
                            >
                                {firstName.charAt(0)}{lastName.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, mb: 0.5, fontSize: { xs: '1.4rem', md: '2rem' } }}>
                                    {getGreeting()}, {firstName}!
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontFamily, opacity: 0.9, fontSize: '0.95rem' }}>
                                        Welcome to your dashboard
                                    </Typography>
                                    <Chip
                                        label={userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                                        size="small"
                                        sx={{ fontFamily, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Tooltip title="Refresh dashboard data">
                            <IconButton
                                onClick={fetchDashboardData}
                                disabled={loading}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: '12px', fontFamily, '& .MuiAlert-message': { fontFamily } }}
                    action={<IconButton size="small" onClick={fetchDashboardData}><RefreshIcon fontSize="small" /></IconButton>}
                >
                    {error}
                </Alert>
            )}

            {loading && (
                <LinearProgress sx={{ mb: 3, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: GREEN_ACCENT }, bgcolor: '#10b98120' }} />
            )}

            {/* Stat Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {statCards.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={statMd} key={index}>
                        <StatCard
                            icon={stat.icon}
                            title={stat.title}
                            value={stat.value}
                            color={stat.color}
                            subtitle={stat.subtitle}
                            loading={loading}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Charts */}
            <Grid container spacing={3}>
                {/* Documents Over Time — Area Chart */}
                <Grid item xs={12} md={8}>
                    <ChartCard
                        title="Documents Over Time"
                        icon={<TrendingUpIcon sx={{ color: GREEN_ACCENT, fontSize: 22 }} />}
                        loading={loading}
                    >
                        {areaData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={areaData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorDocs" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={GREEN_ACCENT} stopOpacity={0.3} />
                                            <stop offset="95%" stopColor={GREEN_ACCENT} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="month" tick={{ fontFamily, fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontFamily, fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <RechartsTooltip
                                        contentStyle={{ fontFamily, borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                        labelStyle={{ fontFamily, fontWeight: 600 }}
                                    />
                                    <Area type="monotone" dataKey="documents" stroke={GREEN_ACCENT} strokeWidth={2.5} fill="url(#colorDocs)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No document data available</Typography>
                        )}
                    </ChartCard>
                </Grid>

                {/* Documents Per User — Pie Chart */}
                <Grid item xs={12} md={4}>
                    <ChartCard
                        title="Documents by User"
                        icon={<DescriptionIcon sx={{ color: '#3b82f6', fontSize: 22 }} />}
                        loading={loading}
                    >
                        {docsPerUser.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie
                                        data={docsPerUser}
                                        cx="50%"
                                        cy="45%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={3}
                                        dataKey="value"
                                        nameKey="name"
                                    >
                                        {docsPerUser.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        contentStyle={{ fontFamily, borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        iconType="circle"
                                        iconSize={8}
                                        formatter={(value) => <span style={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{value}</span>}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No user data available</Typography>
                        )}
                    </ChartCard>
                </Grid>

                {/* Reminders by Status — Bar Chart */}
                <Grid item xs={12} md={6}>
                    <ChartCard
                        title="Reminders by Status"
                        icon={<AlarmIcon sx={{ color: '#8b5cf6', fontSize: 22 }} />}
                        loading={loading}
                    >
                        {remindersByStatus.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={remindersByStatus} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="name" tick={{ fontFamily, fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontFamily, fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <RechartsTooltip
                                        contentStyle={{ fontFamily, borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                                        labelStyle={{ fontFamily, fontWeight: 600 }}
                                    />
                                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={50}>
                                        {remindersByStatus.map((entry, index) => (
                                            <Cell key={`bar-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No reminder data available</Typography>
                        )}
                    </ChartCard>
                </Grid>

                {/* Notifications Summary — simple stat card style */}
                <Grid item xs={12} md={6}>
                    <ChartCard
                        title="Overview"
                        icon={<NotificationsActiveIcon sx={{ color: '#f59e0b', fontSize: 22 }} />}
                        loading={loading}
                    >
                        <Grid container spacing={3} sx={{ width: '100%', px: 1 }}>
                            {[
                                { label: 'Documents', value: widgets.total_documents ?? 0, color: '#10b981', icon: DescriptionIcon },
                                { label: 'Reminders', value: widgets.total_reminders ?? 0, color: '#8b5cf6', icon: AlarmIcon },
                                { label: 'Notifications', value: widgets.total_notifications ?? 0, color: '#f59e0b', icon: NotificationsActiveIcon },
                                ...(isAdmin ? [{ label: 'Team Members', value: widgets.total_users ?? 0, color: '#3b82f6', icon: PeopleIcon }] : []),
                            ].map((item, i) => (
                                <Grid item xs={6} key={i}>
                                    <Box sx={{ textAlign: 'center', py: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${item.color}12` }}>
                                                <item.icon sx={{ fontSize: 24, color: item.color }} />
                                            </Box>
                                        </Box>
                                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                                            {item.value}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </ChartCard>
                </Grid>
            </Grid>
        </Box>
    );
}
